import React, { useEffect, useState, useCallback } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
  MDBSwitch,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import "./BatchManagerPanel.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", { hour12: false });
}

function scheduleLabel(job) {
  if (job.jobType === "WORKER") return `cada 30 s`;
  return `${String(job.schedHour).padStart(2, "0")}:${String(job.schedMinute).padStart(2, "0")} h`;
}

function StatusBadge({ status }) {
  if (!status) return <MDBBadge color="secondary">Sin ejecuciones</MDBBadge>;
  const map = {
    SUCCESS: "success",
    ERROR:   "danger",
    RUNNING: "warning",
  };
  return <MDBBadge color={map[status] || "secondary"}>{status}</MDBBadge>;
}

function AppBadge({ appCode }) {
  const map = { CUBE: "dark", MFM: "info", SSO: "primary" };
  return <MDBBadge color={map[appCode] || "secondary"} light>{appCode}</MDBBadge>;
}

function TypeBadge({ jobType }) {
  return (
    <MDBBadge color={jobType === "WORKER" ? "warning" : "primary"} light>
      {jobType}
    </MDBBadge>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function BatchManagerPanel({ onLoading }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [runsPage, setRunsPage] = useState(1);
  const [runsLoading, setRunsLoading] = useState(false);
  const [editHour, setEditHour] = useState("");
  const [editMinute, setEditMinute] = useState("");
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const RUNS_PAGE_SIZE = 10;

  // ─── Cargar jobs ────────────────────────────────────────────────────────────

  const loadJobs = useCallback(async () => {
    setLoading(true);
    if (onLoading) onLoading(true);
    try {
      const resp = await doFetch("CUBE", "getBatchJobs", { method: "GET" });
      const json = await resp?.json?.().catch(() => null);
      if (json?.STATE === "SUCCESS") {
        setJobs(json.DATA || []);
      } else {
        toast.error("Error cargando procesos batch: " + (json?.MESSAGE || "desconocido"));
      }
    } catch (err) {
      toast.error("Error de red al cargar procesos batch");
    } finally {
      setLoading(false);
      if (onLoading) onLoading(false);
    }
  }, [onLoading]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // ─── Abrir detalle ──────────────────────────────────────────────────────────

  function openDetail(job) {
    setSelectedJob(job);
    setEditHour(String(job.schedHour));
    setEditMinute(String(job.schedMinute));
    setRunsPage(1);
    setRuns([]);
    loadRuns(job.jobKey, 1);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSelectedJob(null);
  }

  // ─── Cargar historial ───────────────────────────────────────────────────────

  async function loadRuns(jobKey, page) {
    setRunsLoading(true);
    try {
      const endpoint = `getBatchJobRuns?jobKey=${encodeURIComponent(jobKey)}&page=${page}&size=${RUNS_PAGE_SIZE}`;
      const resp = await doFetch("CUBE", endpoint, { method: "GET" });
      const json = await resp?.json?.().catch(() => null);
      if (json?.STATE === "SUCCESS") {
        setRuns(json.DATA || []);
        setRunsTotal(json.TOTAL || 0);
        setRunsPage(page);
      }
    } catch (err) {
      // silencioso
    } finally {
      setRunsLoading(false);
    }
  }

  // ─── Toggle enabled inline ──────────────────────────────────────────────────

  async function toggleEnabled(job, newEnabled) {
    try {
      const body = {
        jobKey:      job.jobKey,
        schedHour:   job.schedHour,
        schedMinute: job.schedMinute,
        schedSecond: job.schedSecond || "0",
        enabled:     newEnabled,
      };
      const resp = await doFetch("CUBE", "updateBatchJobConfig", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await resp?.json?.().catch(() => null);
      if (json?.STATE === "SUCCESS") {
        toast.success(`Job ${job.displayName} ${newEnabled ? "habilitado" : "deshabilitado"}`);
        setJobs(prev => prev.map(j => j.jobKey === job.jobKey ? { ...j, enabled: newEnabled } : j));
        if (selectedJob?.jobKey === job.jobKey) {
          setSelectedJob(prev => ({ ...prev, enabled: newEnabled }));
        }
      } else {
        toast.error("Error: " + (json?.MESSAGE || "desconocido"));
      }
    } catch (err) {
      toast.error("Error de red");
    }
  }

  // ─── Guardar schedule ───────────────────────────────────────────────────────

  async function saveSchedule() {
    if (!selectedJob) return;
    setSaving(true);
    try {
      const body = {
        jobKey:      selectedJob.jobKey,
        schedHour:   editHour,
        schedMinute: editMinute,
        schedSecond: selectedJob.schedSecond || "0",
        enabled:     selectedJob.enabled,
      };
      const resp = await doFetch("CUBE", "updateBatchJobConfig", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await resp?.json?.().catch(() => null);
      if (json?.STATE === "SUCCESS") {
        toast.success("Schedule actualizado correctamente");
        const updated = { ...selectedJob, schedHour: editHour, schedMinute: editMinute };
        setSelectedJob(updated);
        setJobs(prev => prev.map(j => j.jobKey === updated.jobKey ? updated : j));
      } else {
        toast.error("Error: " + (json?.MESSAGE || "desconocido"));
      }
    } catch (err) {
      toast.error("Error de red");
    } finally {
      setSaving(false);
    }
  }

  // ─── Disparar manualmente ───────────────────────────────────────────────────

  async function triggerNow() {
    if (!selectedJob) return;
    setTriggering(true);
    try {
      const resp = await doFetch(
        "CUBE",
        `triggerBatchJob?jobKey=${encodeURIComponent(selectedJob.jobKey)}`,
        { method: "POST" }
      );
      const json = await resp?.json?.().catch(() => null);
      if (json?.STATE === "SUCCESS") {
        toast.success("Job ejecutado correctamente");
        // Recargar historial para mostrar nueva ejecución
        loadRuns(selectedJob.jobKey, 1);
        // Refrescar lista principal
        loadJobs();
      } else {
        toast.error((json?.MESSAGE || "Error al ejecutar el job"));
      }
    } catch (err) {
      toast.error("Error de red");
    } finally {
      setTriggering(false);
    }
  }

  // ─── Paginación ─────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(runsTotal / RUNS_PAGE_SIZE);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="batch-manager-panel mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <MDBIcon fas icon="cogs" className="me-2" />
          Procesos batch
        </h5>
        <MDBBtn size="sm" color="light" onClick={loadJobs} disabled={loading}>
          {loading ? <MDBSpinner size="sm" /> : <MDBIcon fas icon="sync-alt" />}
          <span className="ms-2">Actualizar</span>
        </MDBBtn>
      </div>

      <MDBCard>
        <MDBCardBody className="p-0">
          <MDBTable responsive hover className="mb-0 batch-table">
            <MDBTableHead>
              <tr>
                <th>Proceso</th>
                <th>App</th>
                <th>Tipo</th>
                <th>Schedule</th>
                <th>Activo</th>
                <th>Última ejecución</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {loading ? "Cargando..." : "No hay procesos batch configurados"}
                  </td>
                </tr>
              )}
              {jobs.map((job) => (
                <tr key={job.jobKey} className="batch-row">
                  <td>
                    <div className="fw-semibold">{job.displayName}</div>
                    <small className="text-muted batch-desc">{job.description}</small>
                  </td>
                  <td><AppBadge appCode={job.appCode} /></td>
                  <td><TypeBadge jobType={job.jobType} /></td>
                  <td className="font-monospace">{scheduleLabel(job)}</td>
                  <td>
                    <MDBSwitch
                      checked={!!job.enabled}
                      onChange={() => toggleEnabled(job, !job.enabled)}
                      label=""
                    />
                  </td>
                  <td className="text-muted small">{formatDate(job.lastRun?.startedAt)}</td>
                  <td><StatusBadge status={job.lastRun?.status} /></td>
                  <td>
                    <MDBBtn
                      size="sm"
                      color="light"
                      onClick={() => openDetail(job)}
                      title="Ver detalle"
                    >
                      <MDBIcon fas icon="search" />
                    </MDBBtn>
                  </td>
                </tr>
              ))}
            </MDBTableBody>
          </MDBTable>
        </MDBCardBody>
      </MDBCard>

      {/* ─── Modal detalle ─────────────────────────────────────────────── */}
      <MDBModal tabIndex="-1" show={showModal} setShow={(v) => { setShowModal(v); if (!v) setSelectedJob(null); }}>
        <MDBModalDialog size="xl">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {selectedJob?.displayName}
                <span className="ms-2"><AppBadge appCode={selectedJob?.appCode} /></span>
                <span className="ms-1"><TypeBadge jobType={selectedJob?.jobType} /></span>
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={closeModal} />
            </MDBModalHeader>

            <MDBModalBody>
              {selectedJob && (
                <div className="row g-3">
                  {/* ── Configuración ─────────────────────────────── */}
                  <div className="col-12 col-md-4">
                    <MDBCard>
                      <MDBCardHeader>
                        <MDBIcon fas icon="sliders-h" className="me-2" />
                        Configuración
                      </MDBCardHeader>
                      <MDBCardBody>
                        <div className="mb-3 d-flex align-items-center gap-2">
                          <span>Activo</span>
                          <MDBSwitch
                            checked={!!selectedJob.enabled}
                            onChange={() => toggleEnabled(selectedJob, !selectedJob.enabled)}
                            label=""
                          />
                        </div>

                        {selectedJob.jobType === "DAILY" ? (
                          <>
                            <div className="mb-2">
                              <label className="form-label small mb-1">Hora (0-23)</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min="0"
                                max="23"
                                value={editHour}
                                onChange={(e) => setEditHour(e.target.value)}
                              />
                            </div>
                            <div className="mb-3">
                              <label className="form-label small mb-1">Minuto (0-59)</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min="0"
                                max="59"
                                value={editMinute}
                                onChange={(e) => setEditMinute(e.target.value)}
                              />
                            </div>
                            <MDBBtn
                              size="sm"
                              onClick={saveSchedule}
                              disabled={saving}
                            >
                              {saving ? <MDBSpinner size="sm" className="me-1" /> : null}
                              Guardar schedule
                            </MDBBtn>
                          </>
                        ) : (
                          <p className="text-muted small">
                            <MDBIcon fas icon="info-circle" className="me-1" />
                            Schedule fijo: cada 30 segundos.<br />
                            Solo se puede habilitar/deshabilitar.
                          </p>
                        )}
                      </MDBCardBody>
                    </MDBCard>
                  </div>

                  {/* ── Historial ──────────────────────────────────── */}
                  <div className="col-12 col-md-8">
                    <MDBCard>
                      <MDBCardHeader className="d-flex justify-content-between align-items-center">
                        <span>
                          <MDBIcon fas icon="history" className="me-2" />
                          Historial de ejecuciones
                        </span>
                        <MDBBtn
                          size="sm"
                          color="success"
                          onClick={triggerNow}
                          disabled={triggering || selectedJob.jobType === "WORKER"}
                          title={selectedJob.jobType === "WORKER" ? "Los workers no se pueden disparar manualmente" : "Ejecutar ahora"}
                        >
                          {triggering ? <MDBSpinner size="sm" className="me-1" /> : <MDBIcon fas icon="play" className="me-1" />}
                          Ejecutar ahora
                        </MDBBtn>
                      </MDBCardHeader>
                      <MDBCardBody className="p-0">
                        {runsLoading ? (
                          <div className="text-center py-3">
                            <MDBSpinner size="sm" />
                          </div>
                        ) : (
                          <>
                            <MDBTable responsive small className="mb-0">
                              <MDBTableHead>
                                <tr>
                                  <th>#</th>
                                  <th>Inicio</th>
                                  <th>Fin</th>
                                  <th>Estado</th>
                                  <th>Registros</th>
                                  <th>Mensaje</th>
                                </tr>
                              </MDBTableHead>
                              <MDBTableBody>
                                {runs.length === 0 ? (
                                  <tr>
                                    <td colSpan={6} className="text-center text-muted py-3">
                                      Sin ejecuciones registradas
                                    </td>
                                  </tr>
                                ) : runs.map((run) => (
                                  <tr key={run.idRun}>
                                    <td className="text-muted small">{run.idRun}</td>
                                    <td className="small">{formatDate(run.startedAt)}</td>
                                    <td className="small">{formatDate(run.completedAt)}</td>
                                    <td><StatusBadge status={run.status} /></td>
                                    <td className="text-center">
                                      {run.recordsAffected != null ? run.recordsAffected : "-"}
                                    </td>
                                    <td className="small text-muted run-msg">
                                      {run.status === "ERROR"
                                        ? <span className="text-danger">{run.errorMessage}</span>
                                        : run.message || "-"}
                                    </td>
                                  </tr>
                                ))}
                              </MDBTableBody>
                            </MDBTable>

                            {/* Paginación */}
                            {totalPages > 1 && (
                              <div className="d-flex justify-content-center gap-2 p-2">
                                <MDBBtn
                                  size="sm"
                                  color="light"
                                  disabled={runsPage <= 1}
                                  onClick={() => {
                                    const p = runsPage - 1;
                                    loadRuns(selectedJob.jobKey, p);
                                  }}
                                >
                                  <MDBIcon fas icon="chevron-left" />
                                </MDBBtn>
                                <span className="small align-self-center">
                                  {runsPage} / {totalPages}
                                </span>
                                <MDBBtn
                                  size="sm"
                                  color="light"
                                  disabled={runsPage >= totalPages}
                                  onClick={() => {
                                    const p = runsPage + 1;
                                    loadRuns(selectedJob.jobKey, p);
                                  }}
                                >
                                  <MDBIcon fas icon="chevron-right" />
                                </MDBBtn>
                              </div>
                            )}
                          </>
                        )}
                      </MDBCardBody>
                    </MDBCard>
                  </div>
                </div>
              )}
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={closeModal}>
                Cerrar
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </div>
  );
}
